<template>
  <div
    class="language-selector"
    :class="{ open, 'language-selector--landing': isLandingVariant }"
    @mouseenter="handleMouseEnter"
    @mouseleave="scheduleDropDownClose"
  >
    <div class="language-selector__current">
      <p @click="toggleDropDown" @focus="openDropDown(true)">
        {{currentLanguage}}<DropdownArrow v-if="isLandingVariant" :open="open" /><span v-else></span>
      </p>
    </div>
    <div class="language-selector__dropdown">
      <template v-if="select.length == 0">
        <ul>
          <li v-if="languages.includes('en')" :class="{selected: this.$i18n.locale == 'en'}" @click="selectLanguage('en')">English</li>
          <li v-if="languages.includes('ua')" :class="{selected: this.$i18n.locale == 'ua'}" @click="selectLanguage('ua')">Українська</li>
          <li v-if="languages.includes('fr')" :class="{selected: this.$i18n.locale == 'fr'}" @click="selectLanguage('fr')">Français</li>
          <li v-if="languages.includes('es')" :class="{selected: this.$i18n.locale == 'es'}" @click="selectLanguage('es')">Español</li>
          <li v-if="languages.includes('pl')" :class="{selected: this.$i18n.locale == 'pl'}" @click="selectLanguage('pl')">Polski</li>
        </ul>
      </template>
      <template v-else>
        <ul>
          <li v-if="languages.includes('en')" :class="{selected: select == 'en'}" @click="selectLanguage('en')">English</li>
          <li v-if="languages.includes('ua')" :class="{selected: select == 'ua'}" @click="selectLanguage('ua')">Українська</li>
          <li v-if="languages.includes('fr')" :class="{selected: select == 'fr'}" @click="selectLanguage('fr')">Français</li>
          <li v-if="languages.includes('es')" :class="{selected: select == 'es'}" @click="selectLanguage('es')">Español</li>
          <li v-if="languages.includes('pl')" :class="{selected: select == 'pl'}" @click="selectLanguage('pl')">Polski</li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script>
import { AmplitudeEvent } from "components/shared/amplitude.js";
import DropdownArrow from './DropdownArrow.vue'

export default {
  components: { DropdownArrow },
  props: {
    select: { type: String, default: "" },
    languages: { type: Array, default: ["en", "ua", "fr", "es", "pl"] },
    isNotWebsiteLang: { type: Boolean, default: false },
    variant: { type: String, default: "default" },
  },
  data: function(){
    return {
      open: false,
      cyrillicFontReady: null,
      dropDownOpenTimer: null,
      dropDownCloseTimer: null,
      pointerInside: false
    }
  },
  mounted(){
    if (this.isLandingVariant && document.fonts && document.fonts.load) {
      this.cyrillicFontReady = document.fonts.load('700 14px Roboto', 'English Українська Polski').catch(() => undefined)
    }
  },
  computed: {
    isLandingVariant(){
      return this.variant === 'landing'
    },
    currentLanguage(){
      if (this.isNotWebsiteLang) {
        return this.formatLocale(this.select)
      }
      return this.formatLocale(this.$i18n.locale)
    }
  },
  methods: {
    selectLanguage(lang){
      if (!this.isNotWebsiteLang) {
        this.$i18n.locale = lang
      }
      this.closeDropDown()
      this.$emit("languageSelected", lang)
      new AmplitudeEvent("Change language")
    },
    formatLocale(lang){
      switch (lang) {
        case "en":
          return "English"
          break;
        case "fr":
          return "Français"
          break;
        case "es":
          return "Español"
          break;
        case "ua":
          return "Українська"
          break;
        case "pl":
          return "Polski"
          break;
      }
    },
    clearDropDownCloseTimer(){
      if (this.dropDownCloseTimer !== null) {
        window.clearTimeout(this.dropDownCloseTimer)
        this.dropDownCloseTimer = null
      }
    },
    clearDropDownOpenTimer(){
      if (this.dropDownOpenTimer !== null) {
        window.clearTimeout(this.dropDownOpenTimer)
        this.dropDownOpenTimer = null
      }
    },
    async openDropDown(force = false){
      if (!this.isLandingVariant) return

      this.clearDropDownCloseTimer()
      if (this.open) return

      if (this.cyrillicFontReady) {
        await this.cyrillicFontReady
      }

      if (force || this.pointerInside) {
        this.open = true
      }
    },
    closeDropDown(){
      this.clearDropDownOpenTimer()
      this.clearDropDownCloseTimer()
      this.open = false
    },
    scheduleDropDownClose(){
      if (!this.isLandingVariant) return

      this.pointerInside = false
      this.clearDropDownOpenTimer()
      this.clearDropDownCloseTimer()
      this.dropDownCloseTimer = window.setTimeout(() => {
        this.open = false
        this.dropDownCloseTimer = null
      }, 130)
    },
    handleMouseEnter(){
      if (!this.isLandingVariant) return

      this.pointerInside = true
      this.clearDropDownCloseTimer()
      if (this.open || this.dropDownOpenTimer !== null) return

      this.dropDownOpenTimer = window.setTimeout(() => {
        this.dropDownOpenTimer = null
        this.openDropDown()
      }, 60)
    },
    toggleDropDown(){
      if (!this.isLandingVariant) {
        this.open = !this.open
        return
      }

      this.clearDropDownOpenTimer()
      if (this.open) {
        this.closeDropDown()
      } else {
        this.openDropDown(true)
      }
    }
  },
  beforeUnmount(){
    this.clearDropDownOpenTimer()
    this.clearDropDownCloseTimer()
  }
}
</script>

<style lang="scss" scoped>
.language-selector {
  position: relative;
  &.open {
    .language-selector__dropdown {
      display: block;
    }

    .language-selector__current {
      opacity: 1;

      span:not(.dropdown-arrow) {
        transform: rotate(180deg);
        margin: 0 0 4px 4px;
      }
    }
  }
}

.language-selector__current {
  font-size: 13px;
  font-weight: bold;
  text-align: right;
  color: rgb(27, 39, 51);
  opacity: .5;
  user-select: none;
  cursor: pointer;

  &:hover {
    opacity: .7;
  }

  p {
    text-transform: uppercase;
  }

  span:not(.dropdown-arrow) {
    background-image: url('../images/language-arrow.svg');
    width: 8px;
    height: 4px;
    display: inline-block;
    margin: 0 0 3px 4px;
  }
}

.language-selector__dropdown {
  display: none;
  position: absolute;
  box-shadow: 0 2px 4px 1px rgba(27, 39, 51, .1);
  background-color: rgb(255, 255, 255);
  padding: 8px 12px;
  right: 0;
  top: 18px;
  border-radius: 3px;

  li {
    font-size: 13px;
    text-transform: uppercase;
    text-align: right;
    color: rgb(27, 39, 51);
    opacity: .5;
    cursor: pointer;
    user-select: none;
    font-weight: bold;
    margin-bottom: 5px;

    &:last-child {
      margin-bottom: 0px;
    }

    &:hover {
      opacity: .7;
    }

    &.selected {
      opacity: 1;
      pointer-events: none;

      &:hover {
        opacity: 1;
      }
    }
  }
}

.language-selector--landing {
  &.open {
    .language-selector__dropdown {
      visibility: visible;
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
      transition: opacity .17s ease, transform .17s ease, visibility 0s;
    }
  }

  .language-selector__dropdown {
    display: block;
    top: 33px;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-4px);
    transition: opacity .17s ease, transform .17s ease, visibility 0s linear .17s;
  }
}
</style>
